import React from "react";
import {connect} from "react-redux";
import memoizeOne from "memoize-one";
import AutoSizer from "react-virtualized-auto-sizer";
import {FixedSizeList, areEqual} from "react-window";
import {Lot} from "@nextlot/core/webcast/data/data_types_definitions";
import {LOT_STATES_MAP, ROUND_TYPES_DISPLAY_NAMES_MAP, RoundTypeEnum} from "@nextlot/core/webcast/data/WebcastDataDomain";
import {TaggedLogger} from "@nextlot/core/utilities";
import {AuctionsManager} from "@nextlot/core/webcast";
import {ReduxStateType} from "@nextlot/core/webcast/services/DataStore";
import {ReduxWebcastAuctionDataType} from "@nextlot/core/webcast/redux/webcast/reducerWebcastAuctionData";


type LotListItemDataType = {
    lots: Array<Lot>,
    selectedLotsIds: Array<number>,
    currentRoundLotsIds: Array<number>,
    filteredLotsIds: Array<number>,
    onClickLotItem: (itemIndex: number, lots:Array<Lot>)=>any,
}


type LotListItemPropsType = {
    data: LotListItemDataType,
    index: number,
    style: {}
};


// React.memo acts as a PureComponent here (https://reactjs.org/blog/2018/10/23/react-v-16-6.html#reactmemo)
function LotListItemMemoCmp(props: LotListItemPropsType) {
    const {index, style} = props;
    const itemData: LotListItemDataType = props.data;
    const currentRoundLotsIds = itemData.currentRoundLotsIds || [];
    const lot: Lot = itemData.lots[index];
    let lotId;

    if (lot) {
        lotId = lot.id;
    }

    const isThisLotSelected = itemData.selectedLotsIds && itemData.selectedLotsIds.indexOf(lotId) > -1;

    if (lot) {
        return (
            <div
                className={`lots-list__item ${isThisLotSelected ? 'selected' : ''} ${(currentRoundLotsIds.indexOf(lotId) > -1) ? 'current-round' : ''}`}
                style={style}>
                <div className="list-item__text-container" onClick={itemData.onClickLotItem(index, itemData.lots)}>
                    <div className="list-item-text__lot-number">{lot.number} &nbsp;</div>
                    <div className="list-item-text__lot-name">{lot.name}</div>
                    <div className="list-item-text__lot-state">({LOT_STATES_MAP.get(lot.state)})</div>
                </div>
                <img src={lot.defaultImageSmUrl} className="list-item__image" alt={`Lot #${lot.number}`}/>
            </div>
        )
    } else {
        return false;
    }
}

// @ts-ignore
const LotListItem = React.memo(LotListItemMemoCmp, areEqual);


const createLotsListItemData = memoizeOne((lots, selectedLotsIds, currentRoundLotsIds, onClickLotItem) => ({
    lots,
    currentRoundLotsIds,
    selectedLotsIds,
    onClickLotItem,
}));


const _logger = TaggedLogger.get('ClerkLotsListCmp');


type CmpPropsType = {
    webcastAuctionData: ReduxWebcastAuctionDataType,
    currentRoundLotsIds: Array<number>,
    enableRoundActions: boolean,
};

type CmpStateType = Partial<{
    selectedLotsIds: Array<number>,
    lastSelectedLotsListIndex: number,
    filteredLots: Array<Lot>,
    hiddenClosedLots: boolean,
}>


class ClerkLotsListCmp extends React.Component<CmpPropsType, CmpStateType> {

    constructor(props) {
        super(props);

        this.state = {
            selectedLotsIds: [],
            lastSelectedLotsListIndex: 0,
            filteredLots: [],
            hiddenClosedLots: false
        }
    }


    handleOnLotItemClick = (itemIndex: number, lots) => (evt) => {
        _logger.debug('.handleClick_LotsListItem: evt:  ctrl:', evt.ctrlKey, 'shift:', evt.shiftKey, 'meta:', evt.metaKey, 'alt:', evt.altKey);

        let newSelectedLotsIds;
        let lastSelectedLotsListIndex: number = this.state.lastSelectedLotsListIndex;
        let lotId: number = lots[itemIndex].id;


        if (evt.shiftKey) {
            const l1 = Math.min(lastSelectedLotsListIndex, itemIndex);
            const l2 = Math.max(lastSelectedLotsListIndex, itemIndex);

            const shiftSelectedLotsIts = [];
            for (let i = l1; i <= l2; i += 1) {
                shiftSelectedLotsIts.push(lots[i].id);
            }

            newSelectedLotsIds = [...this.state.selectedLotsIds, ...shiftSelectedLotsIts];
        } else if (evt.altKey || evt.metaKey) {
            if (this.state.selectedLotsIds.includes(lotId)) {
                newSelectedLotsIds = this.state.selectedLotsIds.filter((lId) => lotId !== lId);
            } else {
                newSelectedLotsIds = [...this.state.selectedLotsIds, lotId];
                lastSelectedLotsListIndex = itemIndex;
            }
        } else {
            if (this.state.selectedLotsIds.includes(lotId)) {
                if (this.state.selectedLotsIds.length > 1) {
                    newSelectedLotsIds = [lotId];
                    lastSelectedLotsListIndex = itemIndex;
                } else {
                    newSelectedLotsIds = [];
                }
            } else {
                newSelectedLotsIds = [lotId];
                lastSelectedLotsListIndex = itemIndex;
            }
        }

        this.setState({
            selectedLotsIds: newSelectedLotsIds,
            lastSelectedLotsListIndex,
        });
    };

    handleDblClick_Pass = () => {
        const lotsIds = [... new Set(this.state.selectedLotsIds)];
        AuctionsManager.currentWebcastAuctionHandler.controller.sendLotsPass(lotsIds);
    };


    handleClick_RoundCreate = (roundType) => {
        const lotsIds = [... new Set(this.state.selectedLotsIds)];
        AuctionsManager.currentWebcastAuctionHandler.controller.sendRoundCreate(roundType, lotsIds);
    };

    handleOnChange = (event) => {
        const webcastAuctionData: ReduxWebcastAuctionDataType = this.props.webcastAuctionData;
        let lots;

        if (this.state.hiddenClosedLots) {
            lots = webcastAuctionData.lots.map(lot => lot).filter(l => l.state === -1);
        } else {
            lots = webcastAuctionData.lots;
        }

        let filteredLots = [];
        let filterLotsArray;
        let results = event.target.value.toLowerCase().split(","); // split expression separated by ","

        for (let i in results) {
            let searchTextString = results[i].trim();
            if (searchTextString.length > 0) { //it's not an empty string
                // check if interval
                if (searchTextString.indexOf('-') > 0) {
                    const results2 = searchTextString.split("-");

                    // accept it only if it has 2 bounds
                    if (results2.length === 2) {
                        let lot1 = lots.map(lot => lot).filter(l => l.number.toLowerCase() === results2[0].toLowerCase())[0];

                        if (lot1) {
                            let lot2 = lots.map(lot => lot).filter(l => l.number.toLowerCase() === results2[1].toLowerCase())[0];

                            if (lot2) {
                                if (lot1.sortIdx < lot2.sortIdx) {
                                    lots.map(lot => {
                                        if (lot.sortIdx >= lot1.sortIdx && lot.sortIdx <= lot2.sortIdx) {
                                            filteredLots = [...filteredLots, ...lot].filter(el => el != null);
                                        }
                                    });
                                }
                            }
                        }
                    }
                } else {
                    // it's not an interval
                    let lot1 = lots.map(lot => lot).filter(l => l.number.toLowerCase() === searchTextString);
                    filteredLots = [...filteredLots, ...lot1].filter(el => el != null);
                }
            }
        }
        // sorted array with all filtered lots
        filterLotsArray = filteredLots.filter((item, index) => filteredLots.indexOf(item) === index).sort((a, b) => a.sortIdx > b.sortIdx ? 1 : -1);

        this.setState({
            filteredLots: filterLotsArray
        });
    };

    handleChange_HideClosedLots = () => {
        this.setState(state => ({
            hiddenClosedLots: !state.hiddenClosedLots
        }));
    };

    _renderActionsButtons() {
        if (this.state.selectedLotsIds.length === 0) {
            // nothing selected
            return null
        }

        const buttonsDefs = [];

        if (this.state.selectedLotsIds.length === 1) {
            const itemsQty = this.props.webcastAuctionData.lotsMapById.get(this.state.selectedLotsIds[0]).itemsQty;
            if (itemsQty > 1) {
                buttonsDefs.push({
                    label: ROUND_TYPES_DISPLAY_NAMES_MAP.get(RoundTypeEnum.SINGLE_NORMAL),
                    roundType: RoundTypeEnum.SINGLE_NORMAL,
                });

                buttonsDefs.push({
                    label: ROUND_TYPES_DISPLAY_NAMES_MAP.get(RoundTypeEnum.SINGLE_BOXLOT),
                    roundType: RoundTypeEnum.SINGLE_BOXLOT,
                });
            } else {
                buttonsDefs.push({
                    label: ROUND_TYPES_DISPLAY_NAMES_MAP.get(RoundTypeEnum.SINGLE_NORMAL),
                    roundType: RoundTypeEnum.SINGLE_NORMAL,
                });
            }

        } else {
            buttonsDefs.push({
                label: ROUND_TYPES_DISPLAY_NAMES_MAP.get(RoundTypeEnum.GROUP_CHOICE),
                roundType: RoundTypeEnum.GROUP_CHOICE,
            });

            buttonsDefs.push({
                label: ROUND_TYPES_DISPLAY_NAMES_MAP.get(RoundTypeEnum.GROUP_ONEMONEY),
                roundType: RoundTypeEnum.GROUP_ONEMONEY,
            });

            buttonsDefs.push({
                label: ROUND_TYPES_DISPLAY_NAMES_MAP.get(RoundTypeEnum.GROUP_TIMESTHEMONEY),
                roundType: RoundTypeEnum.GROUP_TIMESTHEMONEY,
            });
        }


        return (
            <div>
                {buttonsDefs.map((btnDef) => {
                    return <button className="btn btn-sm btn-info ms-3" type="button"
                                   onClick={this.handleClick_RoundCreate.bind(this, btnDef.roundType)}
                                   key={btnDef.roundType}>{btnDef.label}</button>;
                })}


                <button className="btn btn-sm btn-outline-warning ms-3" type="button" disabled={false}
                        onDoubleClick={this.handleDblClick_Pass}>Pass
                </button>
            </div>
        )
    }


    render() {
        _logger.debug('.render()', this.props);
        const webcastAuctionData: ReduxWebcastAuctionDataType = this.props.webcastAuctionData;
        let lots;

        if (this.state.filteredLots.length > 0) {
            // check if are lots that are matching the search
            if (this.state.hiddenClosedLots) {
                lots = this.state.filteredLots.map(lot => lot).filter(l => l.state === -1);
            } else {
                lots = this.state.filteredLots;
            }
        } else {
            // all the lots
            if (this.state.hiddenClosedLots) {
                // only open lots
                lots = webcastAuctionData.lots.map(lot => lot).filter(l => l.state === -1);
            } else {
                lots = webcastAuctionData.lots;
            }
        }

        const itemData = createLotsListItemData(lots, this.state.selectedLotsIds, this.props.currentRoundLotsIds, this.handleOnLotItemClick);

        return (
            <div className="clerk-lots-list-root-container">

                <div className="clerk-lots-list__actions-header-container">
                    <div>
                        Filter by Lot # &nbsp;
                        <input type="text" onChange={this.handleOnChange.bind(this)}/>
                    </div>

                    {this.props.enableRoundActions && this._renderActionsButtons()}
                </div>


                <div className="clerk-lots-list__list-container-wrapper">
                    <AutoSizer>
                        {({height, width}) => (
                            <FixedSizeList
                                className="clerk-lots-list__list-container"
                                height={height}
                                width={width}
                                itemCount={lots.length}
                                itemSize={42}
                                itemData={itemData}
                                itemKey={(index, itemData) => itemData.lots[index] ? itemData.lots[index].id : Math.random()}
                            >
                                {LotListItem}
                            </FixedSizeList>
                        )}
                    </AutoSizer>
                </div>

                <div className="clerk-lots-list__actions-footer-container">
                    <span>
                        {lots.length} Lots
                    </span>
                    <label className="align-middle mb-0">
                        <input type="checkbox" value={1} onChange={this.handleChange_HideClosedLots}
                               checked={this.state.hiddenClosedLots}/>
                        &nbsp;
                        Hide Closed Lots</label>
                </div>

            </div>
        );
    }
}


export default connect(({activeWebcast}: ReduxStateType) => {
    return {
        webcastAuctionData: activeWebcast.webcastAuctionData,
        currentRoundLotsIds: activeWebcast.webcastCurrentRound.lotsIds,
        enableRoundActions: activeWebcast.webcastStatus.biddingFunctionsEnabled && !(activeWebcast.webcastCurrentRoundBidding.askingBidValue > 0 || activeWebcast.webcastCurrentRoundBidding.highestBid),
    };
})(ClerkLotsListCmp);


